/**
 * Like all shared components in console, if this does not fit your needs, please feel empowered to update/enhance
 * But make sure to update the storybook, tests and keep in mind the accessibility aspects of the update.
 *
 * If you find yourself doing a nested css query to find and change the visuals of this component, feel free to update
 * this component directly.
 */

import { isArray } from 'lodash'
import { ReactNode } from 'react'
import Skeleton from 'react-skeleton-loader'

import InlineErrorMessage, { ErrorMessageProps } from '@/components/InlineErrorMessage/InlineErrorMessage'

// Copied from Skeleton-loader
interface SkeletonOptions {
  width?: string
  height?: string
  widthRandomness?: number
  heightRandomness?: number
  borderRadius?: string
  color?: string
  count?: number
  animated?: boolean
}

type ObserverResult = {
  isIdle: boolean
  isLoading: boolean
  isRefetching?: boolean
  isFetching?: boolean
  isError: boolean
  error: any
  data: any
}

type InlineLoaderProps = {
  className?: string
  useQueryResult: ObserverResult | Array<ObserverResult>
  loaderComponent?: ReactNode
  loaderOptions?: SkeletonOptions
  errorOptions?: ErrorMessageProps
  renderChildrenOnError?: boolean
  children: (data: any, errors: any) => ReactNode
  showLoadingOnFetch?: boolean
  overloadIsLoading?: boolean
}

const InlineLoader = ({
  className,
  children,
  useQueryResult,
  loaderOptions,
  errorOptions,
  // TODO put more thought on this as we may have "render children when an error looks like X"
  renderChildrenOnError,
  loaderComponent,
  showLoadingOnFetch = false,
  overloadIsLoading = false
}: InlineLoaderProps) => {
  let isLoading: any = false
  let isError: any = false
  let data: any = []
  let errorObject: any

  // Shortcut and don't display anything if status is 'idle'
  // Idle = has not yet been enabled
  if ('isIdle' in useQueryResult && useQueryResult.isIdle) {
    return null
  }

  if (isArray(useQueryResult)) {
    isLoading =
      useQueryResult.find(res => res.isLoading || (showLoadingOnFetch && (res.isRefetching || res.isFetching))) ?? false
    isError = useQueryResult.find(res => res.isError) ?? false
    data = useQueryResult.map(res => res.data)
    errorObject = useQueryResult.map(res => res.error)
  } else {
    isLoading =
      useQueryResult.isLoading || (showLoadingOnFetch && (useQueryResult.isRefetching || useQueryResult.isFetching))
    isError = useQueryResult.isError
    data = useQueryResult.data
    errorObject = useQueryResult.error
  }

  if (isLoading || overloadIsLoading) {
    if (loaderComponent) {
      return loaderComponent
    } else {
      return <Skeleton {...loaderOptions} />
    }
  }

  if (isError && !renderChildrenOnError) {
    return <InlineErrorMessage {...errorOptions} className={className} />
  }

  // If you use this with the default fetch in console, the data comes back as `data.data`
  return <>{children(data?.data || data, errorObject)}</>
}

InlineLoader.displayName = 'InlineLoader'

export default InlineLoader
